import {
  BoxGeometry,
  Clock,
  Color,
  ConeGeometry,
  DirectionalLight,
  Fog,
  Group,
  HemisphereLight,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  WebGLRenderer,
  type Material,
} from 'three';
import { VIEW_HEIGHT, scrollSpeedFor } from '../data/tuning';
import { buildLaChiva } from './models/la-chiva';
import { collectRenderedObjects, type RenderedObject } from './scene-info';
import { CAMERA_DISTANCE, FOV_DEGREES, REDUCED_PARALLAX } from './scene-math';
import { WORLD_1_DAWN } from './themes/world-1';
import { registerRenderer, releaseRenderer } from './renderer-registry';
import { attachView, type ViewInfo } from './view';

/**
 * Title backdrop: La Chiva flying over the Aburrá valley at dawn (GDD §4.1, §10.3, §14.1).
 * Low-poly, flat shading, side camera with real depth parallax. Placeholder art until the
 * World 1 art issue fixes the palette (GDD §14.2).
 */

const FOV = FOV_DEGREES;
const SCROLL_SPEED = scrollSpeedFor('1-1'); // the title flies over World 1 (GDD §5.4)

const PALETTE = {
  // Colours of the dawn theme of world 1 (GDD §14.2, issue #68), so the title and OP-101 match.
  hillNear: WORLD_1_DAWN.layers.find((layer) => layer.name === 'layer.mid')!.colors[1]!,
  hillMid: WORLD_1_DAWN.layers.find((layer) => layer.name === 'layer.mid')!.colors[0]!,
  hillFar: WORLD_1_DAWN.layers.find((layer) => layer.name === 'layer.far')!.colors[0]!,
  brick: WORLD_1_DAWN.layers.find((layer) => layer.name === 'layer.mid')!.colors[2]!,
  cloud: 0xfff4e6,
} as const;

const flat = (color: number) => new MeshLambertMaterial({ color, flatShading: true });

function buildHillLayer(count: number, spacing: number, z: number, color: number, maxHeight: number) {
  const layer = new Group();
  const material = flat(color);
  for (let i = 0; i < count; i++) {
    const height = maxHeight * (0.55 + 0.45 * Math.abs(Math.sin(i * 1.7 + z)));
    const hill = new Mesh(new ConeGeometry(spacing * 0.75, height, 5), material);
    hill.position.set(i * spacing, height / 2, z);
    hill.rotation.y = i * 0.9;
    layer.add(hill);
  }
  return { layer, width: count * spacing };
}

function buildHouses(count: number, spacing: number, z: number) {
  const layer = new Group();
  const material = flat(PALETTE.brick);
  for (let i = 0; i < count; i++) {
    const house = new Mesh(new BoxGeometry(0.9, 0.6 + (i % 3) * 0.3, 0.9), material);
    house.position.set(i * spacing, 0.4 + (i % 3) * 0.15, z);
    layer.add(house);
  }
  return { layer, width: count * spacing };
}

function buildClouds(count: number, spacing: number, z: number) {
  const layer = new Group();
  const material = flat(PALETTE.cloud);
  for (let i = 0; i < count; i++) {
    const cloud = new Mesh(new IcosahedronGeometry(0.8 + (i % 2) * 0.5, 0), material);
    cloud.position.set(i * spacing, 9 + (i % 3) * 1.2, z);
    cloud.scale.set(2, 0.8, 1);
    layer.add(cloud);
  }
  return { layer, width: count * spacing };
}

interface ParallaxLayer {
  layer: Group;
  width: number;
  factor: number; // fraction of the gameplay scroll speed
}

export interface TitleScene {
  /** The camera and canvas of the logical view, for the test hooks (issue #65). */
  info(): ViewInfo & {
    objects: RenderedObject[];
    trace: never[];
    drawCalls: number;
    triangles: number;
    frame: { calls: number; triangles: number };
    maxFrame: { calls: number; triangles: number };
    sceneObjectCount: number;
    boss: null;
    cameraShakes: number;
    flashes: number;
    layerScreenX: { far: number; mid: number };
    addedNames: string[];
    theme: { id: string; variant: string; sky: number; fog: number; background: number; sceneFog: number | null };
    themeTransition: null;
    memory: { geometries: number; textures: number };
  };
  render(alpha: number): void;
  /** The title backdrop has no level theme to switch: it throws until a level is loaded (issue #68). */
  requestTheme(id: string): void;
  /** The title backdrop has no columns: it throws until a level is loaded (issue #69). */
  coverage(objectPath: string): number;
  columnParts(objectPath: string): { min: [number, number, number]; max: [number, number, number] }[];
  start(): void;
  stop(): void;
  dispose(): void;
}

export function createTitleScene(container: HTMLElement, options: { reducedMotion: boolean }): TitleScene {
  const renderer = registerRenderer(new WebGLRenderer({ antialias: true }));
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.domElement.dataset.testid = 'game-canvas';
  renderer.domElement.setAttribute('aria-hidden', 'true');
  container.appendChild(renderer.domElement);

  // World 1 palette (GDD §14.2, issue #68): the title backdrop shares the dawn theme of OP-101.
  const theme = WORLD_1_DAWN;
  const scene = new Scene();
  scene.background = new Color(theme.sky);
  scene.fog = new Fog(theme.fog.color, 30, 110);

  const camera = new PerspectiveCamera(FOV, 16 / 9, 0.1, 300);
  camera.position.set(0, VIEW_HEIGHT / 2, CAMERA_DISTANCE);
  camera.lookAt(0, VIEW_HEIGHT / 2, 0);

  scene.add(new HemisphereLight(theme.lights.hemisphere.sky, theme.lights.hemisphere.ground, theme.lights.hemisphere.intensity));
  const sun = new DirectionalLight(theme.lights.sun.color, theme.lights.sun.intensity);
  sun.position.set(-10, 20, 12);
  scene.add(sun);

  const ground = new Mesh(new PlaneGeometry(400, 200), flat(theme.ground));
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(0, 0, -60);
  scene.add(ground);

  const layers: ParallaxLayer[] = [
    { ...buildHillLayer(14, 18, -60, PALETTE.hillFar, 26), factor: 0.1 },
    { ...buildHillLayer(16, 10, -15, PALETTE.hillMid, 12), factor: 0.35 },
    { ...buildHouses(30, 3.2, -6), factor: 0.6 },
    { ...buildHillLayer(12, 9, 3, PALETTE.hillNear, 2.5), factor: 1.2 },
    { ...buildClouds(10, 14, -25), factor: 0.25 },
  ];
  for (const { layer, width } of layers) {
    layer.position.x = -width / 2;
    scene.add(layer);
  }

  // The same model as the flight (issue #66).
  const chiva = buildLaChiva();
  const plane = chiva.group;
  const propeller = chiva.parts.propeller;
  plane.position.set(-3, VIEW_HEIGHT / 2, 0);
  scene.add(plane);

  const view = attachView(container, renderer, camera);

  const clock = new Clock();
  let running = false;
  const maximum = { calls: 0, triangles: 0 };
  const draw = () => {
    renderer.render(scene, camera);
    maximum.calls = Math.max(maximum.calls, renderer.info.render.calls);
    maximum.triangles = Math.max(maximum.triangles, renderer.info.render.triangles);
  };
  const objectCount = () => {
    let count = 0;
    scene.traverse(() => { count += 1; });
    return count;
  };

  const frame = () => {
    const dt = Math.min(clock.getDelta(), 0.1);
    const t = clock.elapsedTime;
    const motion = options.reducedMotion ? REDUCED_PARALLAX : 1;

    for (const entry of layers) {
      entry.layer.position.x -= SCROLL_SPEED * entry.factor * dt * motion;
      if (entry.layer.position.x < -entry.width) entry.layer.position.x += entry.width / 2;
    }
    const bob = options.reducedMotion ? 0 : 1;
    plane.position.y = VIEW_HEIGHT / 2 + Math.sin(t * 1.6) * 0.6 * bob;
    plane.rotation.z = Math.cos(t * 1.6) * 0.12 * bob;
    propeller.rotation.x += dt * 40;

    draw();
  };

  return {
    info: () => ({
      ...view.info(),
      objects: collectRenderedObjects(scene),
      trace: [],
      drawCalls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      frame: { calls: renderer.info.render.calls, triangles: renderer.info.render.triangles },
      maxFrame: { ...maximum }, sceneObjectCount: objectCount(),
      boss: null,
      layerScreenX: { far: 0, mid: 0 },
      cameraShakes: 0,
      flashes: 0,
      addedNames: [],
      theme: { id: theme.id, variant: theme.variant, sky: theme.sky, fog: theme.fog.color, background: new Color(theme.sky).getHex(), sceneFog: scene.fog ? new Color(theme.fog.color).getHex() : null },
      themeTransition: null,
      memory: { geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures },
    }),
    render: draw,
    requestTheme(id) {
      throw new Error(`No level theme on the title screen (requested "${id}")`);
    },
    coverage(objectPath) {
      throw new Error(`No columns on the title screen (requested "${objectPath}")`);
    },
    columnParts(objectPath) {
      throw new Error(`No columns on the title screen (requested "${objectPath}")`);
    },
    start() {
      if (running) return;
      running = true;
      clock.start();
      renderer.setAnimationLoop(frame);
    },
    stop() {
      running = false;
      renderer.setAnimationLoop(null);
    },
    dispose() {
      this.stop();
      view.dispose();
      chiva.dispose();
      scene.traverse((object) => {
        if (object instanceof Mesh) {
          object.geometry.dispose();
          (object.material as Material).dispose();
        }
      });
      releaseRenderer(renderer);
      renderer.domElement.remove();
    },
  };
}
